import { useState, useEffect, useRef } from 'react';

/* ── Backend URL resolution — same logic as the rest of the app ──
   Empty string means same-origin; in dev REACT_APP_BACKEND_URL points at the
   local backend (no CRA proxy), in prod it points at the Railway API. */
const BACKEND_URL = (() => {
  const url = process.env.REACT_APP_BACKEND_URL || '';
  if (!url) return '';
  return url.startsWith('http') ? url : 'https://' + url;
})();

const THREAT_INTEL_CACHE_KEY = 'acodia_threat_intel_cache';
const CACHE_MAX_AGE_MS = 3600 * 1000; // 1 hour — matches backend cache TTL

const SEVERITY_STYLE = {
  CRITICAL: { bg: 'var(--danger)',        fg: '#1A0606' },
  HIGH:     { bg: 'var(--warn)',          fg: '#1A1006' },
  MEDIUM:   { bg: 'rgba(234,179,8,0.85)', fg: '#1A1A06' },
};

const CARD_HEIGHT = 52;

/* Category bar colours — applied by index to payload.threat_categories,
   preserving the original visual palette and order. */
const CATEGORY_COLORS = [
  'var(--danger)',
  'rgba(248,113,113,0.8)',
  'var(--warn)',
  'var(--a)',
  'var(--violet)',
];

/* ── Data hook — sessionStorage hydrate, then background refresh ──
   Returns the last good payload immediately (no spinner) and quietly swaps in
   fresh data when the fetch resolves. Silent on failure: keeps showing whatever
   we already have (cached or null). */
function useThreatIntel() {
  const [data, setData] = useState(() => {
    try {
      const cached = JSON.parse(sessionStorage.getItem(THREAT_INTEL_CACHE_KEY));
      if (cached && Date.now() - cached.ts < CACHE_MAX_AGE_MS) return cached.payload;
    } catch (e) { /* ignore parse/quota errors */ }
    return null;
  });

  useEffect(() => {
    let cancelled = false;
    fetch(`${BACKEND_URL}/api/public/threat-intel`)
      .then((res) => (res.ok ? res.json() : Promise.reject(res.status)))
      .then((payload) => {
        if (cancelled) return;
        setData(payload);
        try {
          sessionStorage.setItem(THREAT_INTEL_CACHE_KEY, JSON.stringify({ ts: Date.now(), payload }));
        } catch (e) { /* ignore quota errors */ }
      })
      .catch((err) => {
        console.warn('Threat intel fetch failed:', err);
        // Silent — keep showing cached/empty data, never break the layout.
      });
    return () => { cancelled = true; };
  }, []);

  return data;
}

/* ── Flip-digit number display (numeric values only) ── */
function FlippingNumber({ value, color }) {
  const prevRef = useRef(value);
  const current = value.toLocaleString('en-GB');
  const previous = (prevRef.current ?? value).toLocaleString('en-GB');
  const maxLen = Math.max(current.length, previous.length);
  const paddedCurrent = current.padStart(maxLen, ' ');
  const paddedPrev = previous.padStart(maxLen, ' ');

  useEffect(() => {
    prevRef.current = value;
  });

  return (
    <span className="lti-digit-row" style={{ color }}>
      {paddedCurrent.split('').map((ch, i) => {
        if (ch === ' ') return null;
        const changed = paddedPrev[i] !== ch;
        return (
          <span
            key={changed ? `c-${i}-${value}` : `s-${i}-${ch}`}
            className={changed ? 'lti-digit lti-digit-flip' : 'lti-digit'}
          >
            {ch}
          </span>
        );
      })}
    </span>
  );
}

/* Static big value (strings like "£1,205" or the "—" placeholder) — same
   typographic treatment as FlippingNumber, no flip animation. */
function StaticBig({ text, color }) {
  return (
    <span className="lti-digit-row" style={{ color }}>
      {String(text).split('').map((ch, i) => (
        <span key={`${i}-${ch}`} className="lti-digit">{ch}</span>
      ))}
    </span>
  );
}

/* Renders a number with the flip effect, or a string statically. */
function BigValue({ display, color }) {
  if (typeof display === 'number') return <FlippingNumber value={display} color={color} />;
  return <StaticBig text={display} color={color} />;
}

/* ── Section 1 — Recent critical CVEs (real, from NVD) ── */
function CveFeed({ cves }) {
  const rowBase = {
    display: 'flex',
    alignItems: 'center',
    gap: '0.65rem',
    height: 44,
    marginBottom: 8,
    padding: '0 0.75rem',
    borderRadius: 6,
    background: 'rgba(255,255,255,0.02)',
    border: '1px solid rgba(255,255,255,0.05)',
  };

  const items = Array.isArray(cves) ? cves.slice(0, 3) : [];

  return (
    <div>
      <p
        className="font-mono uppercase"
        style={{ fontSize: '0.65rem', color: 'var(--ts)', marginBottom: '0.5rem', letterSpacing: '0.12em' }}
      >
        New Vulnerabilities Today
      </p>
      <div style={{ minHeight: CARD_HEIGHT * 3 - 8 }}>
        {items.length === 0 ? (
          <div style={{ ...rowBase, justifyContent: 'center' }}>
            <span className="font-body" style={{ fontSize: '0.72rem', color: 'var(--ts)' }}>
              Latest CVE data temporarily unavailable. Refreshing soon.
            </span>
          </div>
        ) : (
          items.map((cve) => {
            const style = SEVERITY_STYLE[cve.severity] || SEVERITY_STYLE.HIGH;
            return (
              <div key={cve.cve_id} style={rowBase}>
                <span
                  className="font-mono"
                  style={{
                    background: style.bg,
                    color: style.fg,
                    padding: '2px 7px',
                    borderRadius: 3,
                    fontSize: '0.55rem',
                    letterSpacing: '0.1em',
                    fontWeight: 700,
                    flexShrink: 0,
                  }}
                >
                  {cve.severity}
                </span>
                <span
                  className="font-body"
                  style={{
                    fontSize: '0.78rem',
                    color: 'var(--white)',
                    flex: 1,
                    minWidth: 0,
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                  }}
                >
                  {cve.vendor_product || cve.cve_id}
                </span>
                <span
                  className="font-mono"
                  style={{ fontSize: '0.6rem', color: 'var(--tm)', flexShrink: 0, letterSpacing: '0.02em' }}
                >
                  {cve.cve_id}
                </span>
                <span
                  className="font-mono"
                  style={{ fontSize: '0.65rem', color: 'var(--ts)', flexShrink: 0 }}
                >
                  {cve.time_ago_label}
                </span>
              </div>
            );
          })
        )}
      </div>
    </div>
  );
}

/* ── Section 2 — Stat cards (real NVD counts + cited UK survey figure) ── */
function Counter({ label, sublabel, display, color }) {
  return (
    <div
      className="lti-counter"
      style={{
        border: '1px solid rgba(255,255,255,0.05)',
        borderRadius: 8,
        padding: '0.75rem 0.75rem 0.65rem',
        background: 'rgba(255,255,255,0.015)',
        display: 'flex',
        flexDirection: 'column',
        gap: '0.35rem',
      }}
    >
      <p
        className="font-mono uppercase"
        style={{ fontSize: '0.55rem', letterSpacing: '0.12em', color: 'var(--ts)', lineHeight: 1.2 }}
      >
        {label}
      </p>
      <BigValue display={display} color={color} />
      <p className="font-body" style={{ fontSize: '0.65rem', color: 'var(--ts)', lineHeight: 1.3 }}>
        {sublabel}
      </p>
    </div>
  );
}

function LiveCounters({ data }) {
  const year = (() => {
    if (data?.last_updated) {
      const y = new Date(data.last_updated).getFullYear();
      if (!Number.isNaN(y)) return y;
    }
    return new Date().getFullYear();
  })();

  // "—" when NVD data is unavailable (both counts zero) or not yet loaded.
  const countsUnavailable =
    !data || (data.cves_this_year === 0 && data.critical_cves_this_month === 0);

  const cveCost = data?.uk_sme_breach_cost_gbp;
  const costDisplay = typeof cveCost === 'number' ? `£${cveCost.toLocaleString('en-GB')}` : '£1,205';

  return (
    <div className="lti-counter-grid">
      <Counter
        label={`CVEs Published in ${year}`}
        sublabel="NVD CVE database · Live data"
        display={countsUnavailable ? '—' : data.cves_this_year}
        color="var(--danger)"
      />
      <Counter
        label="Avg Cost of a UK SME Breach"
        sublabel={data?.uk_sme_breach_cost_source || 'UK Cyber Security Breaches Survey 2025'}
        display={costDisplay}
        color="var(--warn)"
      />
      <Counter
        label="Critical CVEs This Month"
        sublabel="NVD CVE database · Last 30 days"
        display={countsUnavailable ? '—' : data.critical_cves_this_month}
        color="var(--a)"
      />
    </div>
  );
}

/* ── Section 3 — Threat categories (ENISA, cited) ── */
function CategoryBars({ categories, source }) {
  const cats = (Array.isArray(categories) ? categories : []).map((c, i) => ({
    label: c.name,
    pct: c.percentage,
    color: CATEGORY_COLORS[i % CATEGORY_COLORS.length],
  }));

  const [widths, setWidths] = useState([]);
  const sig = cats.map((c) => c.pct).join(',');

  // Stagger-animate bars from 0 to their percentage whenever the data arrives/changes.
  useEffect(() => {
    if (!cats.length) { setWidths([]); return; }
    setWidths(cats.map(() => 0));
    const timers = cats.map((c, i) =>
      setTimeout(() => {
        setWidths((prev) => {
          const next = [...prev];
          next[i] = c.pct;
          return next;
        });
      }, 150 * i),
    );
    return () => timers.forEach(clearTimeout);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sig]);

  return (
    <div>
      <p
        className="font-mono uppercase"
        style={{ fontSize: '0.65rem', color: 'var(--ts)', marginBottom: '0.6rem', letterSpacing: '0.12em' }}
      >
        Active Threat Categories
      </p>
      <div className="flex flex-col" style={{ gap: '0.55rem' }}>
        {cats.map((c, i) => (
          <div key={c.label}>
            <div className="flex items-baseline justify-between" style={{ marginBottom: 4 }}>
              <span className="font-body" style={{ fontSize: '0.7rem', color: 'var(--tm)' }}>
                {c.label}
              </span>
              <span className="font-mono" style={{ fontSize: '0.7rem', color: c.color, fontWeight: 600 }}>
                {c.pct}%
              </span>
            </div>
            <div style={{ height: 4, borderRadius: 2, background: 'rgba(255,255,255,0.06)', overflow: 'hidden' }}>
              <div
                style={{
                  height: '100%',
                  width: `${widths[i] ?? 0}%`,
                  background: c.color,
                  borderRadius: 2,
                  transition: 'width 1200ms cubic-bezier(0.22, 1, 0.36, 1)',
                }}
              />
            </div>
          </div>
        ))}
      </div>
      {source && (
        <p
          className="font-mono uppercase"
          style={{ fontSize: '0.55rem', color: 'var(--ts)', letterSpacing: '0.12em', marginTop: '0.7rem' }}
        >
          Source: {source}
        </p>
      )}
    </div>
  );
}

/* ── Main panel ── */
export function LiveThreatIntelligence() {
  const data = useThreatIntel();

  return (
    <div
      data-testid="live-threat-intelligence"
      className="lti-panel"
      style={{
        position: 'relative',
        width: '100%',
        minHeight: 480,
        borderRadius: 16,
        padding: '1.5rem',
        background: 'rgba(4,3,8,0.95)',
        border: '1px solid rgba(139,92,246,0.2)',
        boxShadow: '0 0 60px rgba(139,92,246,0.08), inset 0 1px 0 rgba(255,255,255,0.04)',
        overflow: 'hidden',
        display: 'flex',
        flexDirection: 'column',
        gap: '1.25rem',
      }}
    >
      {/* Scanline overlay */}
      <div
        aria-hidden
        style={{
          position: 'absolute',
          inset: 0,
          pointerEvents: 'none',
          background:
            'repeating-linear-gradient(0deg, rgba(0,0,0,0.03) 0, rgba(0,0,0,0.03) 1px, transparent 1px, transparent 2px)',
          borderRadius: 16,
          mixBlendMode: 'overlay',
        }}
      />

      {/* Header */}
      <div className="relative flex items-center" style={{ gap: '0.55rem' }}>
        <span
          style={{
            width: 8,
            height: 8,
            borderRadius: '50%',
            background: 'var(--ok)',
            boxShadow: '0 0 10px var(--ok)',
            animation: 'pulseDot 1.4s ease-in-out infinite',
          }}
        />
        <span
          className="font-mono"
          style={{ fontSize: '0.6rem', letterSpacing: '0.15em', color: 'var(--ok)', textTransform: 'uppercase' }}
        >
          Live Threat Intelligence
        </span>
      </div>

      <div className="relative"><CveFeed cves={data?.recent_cves} /></div>
      <div className="relative"><LiveCounters data={data} /></div>
      <div className="relative"><CategoryBars categories={data?.threat_categories} source={data?.threat_categories_source} /></div>

      {/* Footer */}
      <div
        className="relative mt-auto"
        style={{ borderTop: '1px solid rgba(255,255,255,0.05)', paddingTop: '0.6rem', textAlign: 'center' }}
      >
        <p className="font-body" style={{ fontSize: '0.6rem', color: 'var(--ts)', fontStyle: 'italic' }}>
          Sources: NVD CVE database · UK Cyber Security Breaches Survey 2025 · ENISA Threat Landscape 2025 · Refreshed hourly
        </p>
      </div>
    </div>
  );
}

export default LiveThreatIntelligence;
